import React from "react"
import AsperaLogo from "@src/media/icons/aspera.png"
import {
  can,
  MAKE_PAYMENTS,
  MANAGE_CREDIT_CARDS,
  MODIFY_PLAN,
  VIEW_INVOICES,
  VIEW_USERS_AND_ROLES,
  VIEW_SETTINGS,
  MODIFY_SETTINGS
} from "@src/helpers/authorization"

const isValidOrganization = organization => organization && organization._id

export const canWhiteLabel = organization =>
  isValidOrganization(organization) && (organization.plan.can_white_label || false)

export const canAccessFestivals = organization =>
  isValidOrganization(organization) && (organization.plan.can_access_festivals || false)

export const canAccessFiles = (user, organization) =>
  !organization || (organization.plan && organization.plan.can_access_files !== false)

/**
 * Which team settings pages the user may access. Shared by the sidebar and the settings routes
 * so that a hidden link can't still be reached by URL.
 */
export const getSettingsAccess = (user, organization) => {
  const isOrganization = isValidOrganization(organization)
  return {
    // Loading the team profile requires view access; editing it is gated on the page.
    teamProfile: isOrganization && can(user, [VIEW_SETTINGS]),
    billing: !isOrganization
      || [VIEW_INVOICES, MANAGE_CREDIT_CARDS, MODIFY_PLAN, MAKE_PAYMENTS].some(permission => can(user, [permission])),
    usersAndRoles: isOrganization && can(user, [VIEW_USERS_AND_ROLES]),
    screenersCustomization: canWhiteLabel(organization) && can(user, [MODIFY_SETTINGS]),
    // Entitlements are a billing resource and downloads an organization setting; each section is gated on the page.
    asperaEntitlements: (canAccessFestivals(organization) || canAccessFiles(user, organization))
      && isOrganization && (can(user, [MAKE_PAYMENTS]) || can(user, [VIEW_SETTINGS]))
  }
}

const createSidebar = (user, organization) => {
  const access = getSettingsAccess(user, organization)
  const canViewUsage = canAccessFiles(user, organization)
  return [
    {
      to: "/settings/apps",
      text: "My Apps",
      icon: "apps",
      show: true,
      label_before: "My Settings"
    },
    {
      to: "/settings/profile",
      text: "Personal Profile",
      icon: "person",
      show: true
    },
    {
      to: "/settings/notifications",
      text: "Notifications",
      icon: "notifications",
      show: true
    },
    {
      to: "/settings/security",
      text: "Security",
      icon: "lock",
      show: true
    },
    {
      to: "/settings/team",
      text: "Team Profile",
      icon: "apartment",
      show: access.teamProfile,
      label_before: "Team Settings"
    },
    {
      to: "/settings/billing",
      text: canViewUsage ? "Billing and Usage" : "Billing",
      icon: "receipt",
      show: access.billing
    },
    {
      to: "/settings/users",
      text: "Users",
      icon: "groups",
      show: access.usersAndRoles
    },
    {
      to: "/settings/roles",
      text: "Roles",
      icon: "verified_user",
      show: access.usersAndRoles
    },
    {
      to: "/settings/aspera",
      text: "Aspera Entitlements",
      icon: (
        <img
          className='cs-img'
          src={AsperaLogo}
          alt='Aspera'
          style={{
            width: '24px',
            height: '24px'
          }}/>
      ),
      show: access.asperaEntitlements,
    },
    {
      to: "/settings/screeners",
      text: "Screeners Customization",
      icon: "ondemand_video",
      show: access.screenersCustomization,
      label_before: "CineSend Files",
    }
  ].filter(opt => opt.show)
}

export default createSidebar
